import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ArrowRight,
  CheckCircle2,
  CreditCard,
  ExternalLink,
  MapPin,
  ShieldAlert,
} from "lucide-react";
import { findPresentialEquivalent, isOnlineEmission, loadCatalogProducts } from "@/lib/catalog";
import { useCnhCheckStore } from "@/stores/cnhCheckStore";
import { formatCurrency } from "@/api/client";
import type { Product } from "@/types/api";

type Step = "asking" | "no-cnh";

/**
 * Global modal mounted at App root. Intercepts every purchase that includes
 * at least one ONLINE certificate product and asks whether the buyer has a
 * CNH emitted after 2017. If not, it proposes the equivalent presential
 * product and the on-site address.
 */
export function CnhCheckDialog() {
  const { open, products, callback, close } = useCnhCheckStore();
  const [confirmed, setConfirmed] = useState(false);
  const [step, setStep] = useState<Step>("asking");
  const [catalog, setCatalog] = useState<Product[]>([]);

  // Load catalog lazily on first open so we can map ONLINE → PRESENTIAL
  useEffect(() => {
    if (!open || catalog.length > 0) return;
    void loadCatalogProducts().then((data) => setCatalog(data)).catch(() => undefined);
  }, [open, catalog.length]);

  // Reset state each time modal opens
  useEffect(() => {
    if (open) {
      setConfirmed(false);
      setStep("asking");
    }
  }, [open]);

  const onlineItems = useMemo(
    () => products.filter((p) => isOnlineEmission(p)),
    [products]
  );

  const presentialReplacement = useMemo(() => {
    if (catalog.length === 0) return null;
    // Map every online item in the basket to its presential equivalent.
    // If any cannot be mapped, flag that and fall back to "talk to support".
    const mapped = onlineItems.map((p) => ({
      online: p,
      presential: findPresentialEquivalent(catalog, p),
    }));
    const unmapped = mapped.filter((m) => !m.presential);
    return { mapped, unmapped, ok: unmapped.length === 0 };
  }, [onlineItems, catalog]);

  // If the dialog opens but there's no online item at all, resolve immediately
  useEffect(() => {
    if (open && onlineItems.length === 0 && callback) {
      callback(products, "confirmed");
      close();
    }
  }, [open, onlineItems.length, products, callback, close]);

  const handleConfirm = () => {
    if (!confirmed) return;
    callback?.(products, "confirmed");
    close();
  };

  const handleSwitchPresential = () => {
    if (!presentialReplacement?.ok || !callback) return;
    const newList = products.map((p) => {
      const match = presentialReplacement.mapped.find((m) => m.online.id === p.id);
      return match?.presential ?? p;
    });
    callback(newList, "switched-presential");
    close();
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      callback?.(products, "canceled");
      close();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        {step === "asking" ? (
          <>
            <DialogHeader>
              <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10">
                <CreditCard className="h-6 w-6 text-primary" />
              </div>
              <DialogTitle className="text-xl">Antes de continuar</DialogTitle>
              <DialogDescription>
                Para emitir este certificado online, a validação de identidade é feita por
                videoconferência usando a <strong className="text-foreground">CNH</strong> (Carteira
                Nacional de Habilitação) emitida após 2017. O sistema valida automaticamente no Serpro.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3 py-2">
              {/* Main checkbox */}
              <label className="flex cursor-pointer items-start gap-3 rounded-xl border-2 border-primary/30 bg-primary/5 p-4 transition-all hover:border-primary/50">
                <Checkbox
                  checked={confirmed}
                  onCheckedChange={(v) => setConfirmed(v === true)}
                  className="mt-0.5 h-5 w-5"
                />
                <div className="flex-1">
                  <p className="text-sm font-bold text-foreground">
                    Eu possuo CNH emitida após 2017
                  </p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    Declaro que tenho uma CNH válida, em meu nome, emitida após 2017 e vou apresentá-la na
                    videoconferência.
                  </p>
                </div>
              </label>
            </div>

            <div className="flex flex-col gap-2 pt-2">
              <Button
                size="lg"
                className="w-full font-bold"
                onClick={handleConfirm}
                disabled={!confirmed}
              >
                <CheckCircle2 className="h-4 w-4 mr-2" /> Continuar com emissão online
                <ArrowRight className="h-4 w-4 ml-auto" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="w-full text-muted-foreground hover:text-destructive"
                onClick={() => setStep("no-cnh")}
              >
                Não tenho CNH ou minha CNH foi emitida antes de 2017
              </Button>
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-warning/15">
                <ShieldAlert className="h-6 w-6 text-warning" />
              </div>
              <DialogTitle className="text-xl">Sem CNH? Sem problema.</DialogTitle>
              <DialogDescription>
                A emissão online exige CNH pós-2017 para a validação biométrica automática. Mas
                você pode emitir <strong className="text-foreground">presencialmente</strong> — aceitamos
                qualquer documento oficial com foto e fé pública dentro da validade.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-2">
              {/* Accepted documents */}
              <div className="rounded-xl bg-success/5 border border-success/20 p-4">
                <p className="text-[11px] font-bold uppercase tracking-wider text-success mb-2">
                  Documentos aceitos presencialmente
                </p>
                <ul className="space-y-1 text-sm text-foreground">
                  {["RG", "CPF", "Carteira de classe profissional (OAB, CRM, CRC, etc)", "Carteira de trabalho", "Passaporte"].map(
                    (doc) => (
                      <li key={doc} className="flex items-center gap-2">
                        <CheckCircle2 className="h-3.5 w-3.5 text-success shrink-0" />
                        <span>{doc}</span>
                      </li>
                    )
                  )}
                </ul>
              </div>

              {/* Address card */}
              <div className="rounded-xl border-2 border-primary/30 bg-primary/5 p-4">
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground">
                    <MapPin className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] uppercase tracking-wider font-bold text-primary">
                      Nosso endereço
                    </p>
                    <p className="font-bold text-foreground text-base leading-tight">
                      Rua da Glória, 108
                    </p>
                    <p className="text-sm text-foreground">Liberdade — São Paulo / SP</p>
                    <a
                      href="https://maps.google.com/?q=Rua+da+Glória+108+Liberdade+São+Paulo+SP"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                    >
                      Ver no mapa <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                </div>
              </div>

              {/* Replacement proposal */}
              {presentialReplacement?.ok && (
                <div className="rounded-xl border bg-card p-4 space-y-3">
                  <p className="text-xs font-bold text-foreground">
                    Podemos trocar pelo equivalente presencial:
                  </p>
                  <ul className="space-y-2">
                    {presentialReplacement.mapped.map((m) => (
                      <li key={m.online.id} className="flex items-start justify-between gap-3 text-xs">
                        <div className="flex-1 min-w-0">
                          <p className="text-muted-foreground line-through truncate">
                            {m.online.name}
                          </p>
                          <p className="font-bold text-foreground truncate">
                            → {m.presential!.name}
                          </p>
                        </div>
                        <span className="tabular-nums text-foreground font-semibold shrink-0">
                          {formatCurrency(m.presential!.priceCents)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="flex flex-col gap-2 pt-2">
              {presentialReplacement?.ok ? (
                <Button size="lg" className="w-full font-bold" onClick={handleSwitchPresential}>
                  Trocar pelo presencial e continuar <ArrowRight className="h-4 w-4 ml-auto" />
                </Button>
              ) : (
                <Button size="lg" className="w-full font-bold" asChild>
                  <a href="https://wa.me/551152397247?text=Ol%C3%A1%2C+quero+emitir+um+certificado+presencialmente">
                    Falar com especialista pelo WhatsApp
                  </a>
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="w-full text-muted-foreground"
                onClick={() => setStep("asking")}
              >
                ← Voltar
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
